import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { imagePreflight } from '../lib/uploads.js';
import { ChatRefCard } from './ChatRefCard.jsx';
import { ChatRefPicker } from './ChatRefPicker.jsx';
import { NewConversationModal } from './NewConversationModal.jsx';

export function ChatView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  // Chat is archived read-only (CJ 2026-07-31). The server also blocks writes;
  // this hides the composer + new-conversation button and shows a banner so it's
  // obvious. Revived only if MONARCH_CHAT_ENABLED flips the injected flag.
  const chatEnabled = typeof window !== 'undefined' && window.__CHAT_ENABLED === true;
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null); // conversation object
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showCompose, setShowCompose] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  async function loadConversations() {
    try {
      const r = await api('GET', '/api/chat/conversations', null, headers);
      setConversations(r.conversations || []);
      // Honor a cross-component hand-off: SendMessageButton in the
      // team-directory profile drops a localStorage entry pointing at
      // a freshly-created 1:1 thread; we auto-open it on mount.
      try {
        const want = localStorage.getItem('monarchOpenConversation');
        if (want) {
          localStorage.removeItem('monarchOpenConversation');
          const target = (r.conversations || []).find(c => String(c.id) === String(want));
          if (target) openConversation(target);
        }
      } catch (_) {}
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadConversations(); const t = setInterval(loadConversations, 30000); return () => clearInterval(t); }, []);

  async function openConversation(c) {
    setSelected(c);
    try {
      const r = await api('GET', `/api/chat/conversations/${c.id}/messages?limit=50`, null, headers);
      setMessages(r.messages || []);
      api('POST', `/api/chat/conversations/${c.id}/read`, {}, headers).catch(() => {});
      // Optimistically zero the unread badge in the list.
      setConversations(cs => cs.map(x => x.id === c.id ? { ...x, unread_count: 0 } : x));
      loadReads(c.id);
    } catch (e) { setErr(e.message || String(e)); }
  }

  // CJ 2026-05-16: read-receipt tracking. /reads returns last_read_at per
  // participant; we render a "Read by …" line under the latest outgoing
  // message they've seen so the sender knows the message landed. Refreshed
  // periodically so receipts trickle in without re-opening the thread.
  const [reads, setReads] = useState([]);
  async function loadReads(convoId) {
    try {
      const r = await api('GET', `/api/chat/conversations/${convoId}/reads`, null, headers);
      setReads(Array.isArray(r?.reads) ? r.reads : []);
    } catch (_) { setReads([]); }
  }
  useEffect(() => {
    if (!selected?.id) return;
    const t = setInterval(() => loadReads(selected.id), 20000);
    return () => clearInterval(t);
  }, [selected?.id]);

  // Refs the user is staging to attach to the next outgoing message.
  // Each entry: { kind: 'recipe' | 'task' | 'knowledge', id, label }
  // Or for images: { kind: 'image', url, name, mime }
  const [stagedRefs, setStagedRefs] = useState([]);
  const [showRefPicker, setShowRefPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadOk, setUploadOk] = useState(false);
  const fileInputRef = React.useRef(null);
  // Conversation-list action state — long-press to open an action sheet
  // (Hide / manager Wipe). Swipe left to reveal Hide directly.
  const [convoActionFor, setConvoActionFor] = useState(null); // conversation
  const [swipedId, setSwipedId] = useState(null);
  const isManagerCurrentUser = window.EmpireRoles.isManager(currentUser);
  const longPressConvoTimer = React.useRef(null);
  const swipeStartX = React.useRef(null);
  function startConvoLongPress(c) {
    if (longPressConvoTimer.current) clearTimeout(longPressConvoTimer.current);
    longPressConvoTimer.current = setTimeout(() => setConvoActionFor(c), 550);
  }
  function cancelConvoLongPress() {
    if (longPressConvoTimer.current) { clearTimeout(longPressConvoTimer.current); longPressConvoTimer.current = null; }
  }
  function onSwipeStart(e, id) {
    swipeStartX.current = (e.touches && e.touches[0]?.clientX) ?? e.clientX ?? null;
  }
  function onSwipeMove(e, id) {
    if (swipeStartX.current == null) return;
    const x = (e.touches && e.touches[0]?.clientX) ?? e.clientX ?? null;
    if (x == null) return;
    const dx = x - swipeStartX.current;
    if (dx < -40 && swipedId !== id) setSwipedId(id);
    if (dx > 20 && swipedId === id) setSwipedId(null);
  }
  function onSwipeEnd() { swipeStartX.current = null; }
  async function hideConversation(c) {
    setConvoActionFor(null); setSwipedId(null);
    if (selected?.id === c.id) setSelected(null);
    setConversations(cs => cs.filter(x => x.id !== c.id));
    try { await api('POST', `/api/chat/conversations/${c.id}/hide`, {}, headers); }
    catch (e) { alert(e.message || 'Hide failed'); loadConversations(); }
  }
  async function wipeConversation(c) {
    setConvoActionFor(null);
    if (!confirm(`Permanently delete the entire history of "${conversationLabel(c)}" for everyone? This cannot be undone.`)) return;
    if (selected?.id === c.id) setSelected(null);
    setConversations(cs => cs.filter(x => x.id !== c.id));
    try { await api('DELETE', `/api/chat/conversations/${c.id}`, null, headers); }
    catch (e) { alert(e.message || 'Wipe failed'); loadConversations(); }
  }
  function conversationLabel(c) {
    return c?.title || (c?.audience_signature || '').replace(/^ind:|^custom:|^loc:|^all/, '') || `Conversation #${c?.id}`;
  }

  async function handlePhotoPick(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file || !selected) return;
    setUploading(true);
    try {
      const { base64, mime, filename } = await imagePreflight(file);
      const r = await api('POST', `/api/chat/uploads/${selected.id}`,
        { filename, mime, base64 }, headers);
      // Include the freshly-minted signed_url so the just-uploaded image
      // renders immediately. The next messages-list fetch will refresh
      // it; the bare `url` is the canonical persisted reference.
      setStagedRefs(s => [...s, { kind: 'image', url: r.url, signed_url: r.signed_url, name: r.filename || filename, mime: r.mime || mime }]);
      setUploadOk(true);
      setTimeout(() => setUploadOk(false), 2500);
    } catch (err) {
      let m = err.message || 'Upload failed';
      try { m = JSON.parse(err.message).error || m; } catch (_) {}
      alert(m);
    } finally {
      setUploading(false);
    }
  }
  // C5 chat search.
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // C4 long-press / right-click action sheet on a message.
  const [actionSheet, setActionSheet] = useState(null); // { message }
  const [editing, setEditing] = useState(null); // { message_id, body }
  const longPressTimer = React.useRef(null);

  function startLongPress(m) {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => setActionSheet({ message: m }), 550);
  }
  function cancelLongPress() {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  }
  async function deleteMessage(m) {
    setActionSheet(null);
    if (!confirm('Delete this message?')) return;
    try {
      await api('DELETE', `/api/chat/messages/${m.id}`, null, headers);
      setMessages(ms => ms.map(x => x.id === m.id ? { ...x, deleted_at: new Date().toISOString(), body: '(message deleted)' } : x));
    } catch (e) { alert(e.message || 'Delete failed'); }
  }
  async function saveEdit() {
    if (!editing || !editing.body.trim()) return;
    try {
      await api('PUT', `/api/chat/messages/${editing.message_id}`, { body: editing.body }, headers);
      setMessages(ms => ms.map(x => x.id === editing.message_id ? { ...x, body: editing.body, edited_at: new Date().toISOString() } : x));
      setEditing(null);
    } catch (e) { alert(e.message || 'Edit failed'); }
  }
  // C3 @mention typeahead.
  const [mentionDropdown, setMentionDropdown] = useState(null); // { query, anchor }
  const [recipients, setRecipients] = useState([]);
  useEffect(() => {
    api('GET', '/api/chat/recipients', null, headers)
      .then(r => setRecipients(r.users || [])).catch(() => {});
  }, []);

  function onDraftChange(value) {
    setDraft(value);
    // Detect a trailing @word the user is composing.
    const m = value.match(/(?:^|\s)@([A-Za-z][\w' -]*)$/);
    if (m) setMentionDropdown({ query: m[1].toLowerCase() });
    else setMentionDropdown(null);
  }
  function pickMention(name) {
    const v = draft.replace(/(?:^|\s)@[A-Za-z][\w' -]*$/, (match) => {
      const lead = match.startsWith(' ') ? ' ' : '';
      return `${lead}@${name} `;
    });
    setDraft(v);
    setMentionDropdown(null);
  }
  async function runSearch(q) {
    setSearchQ(q);
    if (q.trim().length < 2) { setSearchResults([]); return; }
    try {
      const r = await api('GET', `/api/chat/search?q=${encodeURIComponent(q)}`, null, headers);
      setSearchResults(r.results || []);
    } catch (e) { setSearchResults([]); }
  }

  async function send() {
    if (!selected || (!draft.trim() && stagedRefs.length === 0)) return;
    setSending(true);
    try {
      const r = await api('POST', `/api/chat/conversations/${selected.id}/messages`,
        { body: draft.trim() || '(see attached)', refs: stagedRefs.map(ref => (
          ref.kind === 'image'
            ? { kind: 'image', url: ref.url, name: ref.name, mime: ref.mime }
            : { kind: ref.kind, id: ref.id }
        )) },
        headers);
      setMessages(m => [...m, r.message]);
      setDraft('');
      setStagedRefs([]);
      setConversations(cs => cs.map(x => x.id === selected.id
        ? { ...x, last_message_at: r.message.created_at, last_body: r.message.body }
        : x));
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      alert(m);
    } finally { setSending(false); }
  }

  if (loading && conversations.length === 0) {
    return <div className="card text-sm text-gray-500">Loading chat…</div>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 12 }} className="chat-grid">
      {/* Conversation list */}
      <div style={{ display: selected && window.innerWidth < 768 ? 'none' : 'block' }}>
        <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
          <div className="text-base font-semibold">Conversations</div>
          {chatEnabled && <button onClick={() => setShowCompose(true)}
            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                      background: 'var(--forest, #1B2B1B)', color: '#fff', border: 'none', borderRadius: 999, cursor: 'pointer' }}>
            + New message
          </button>}
        </div>
        <div className="card" style={{ marginBottom: 8, padding: 8 }}>
          <input value={searchQ} onChange={e => runSearch(e.target.value)}
            placeholder="Search messages…"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }} />
        </div>
        {searchQ.trim().length >= 2 && (
          <div className="card" style={{ padding: 0, marginBottom: 8 }}>
            <div style={{ padding: '8px 12px', fontSize: 11, color: '#9e9e9e',
                            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
                            borderBottom: '1px solid #f0f0f0' }}>
              Search results ({searchResults.length})
            </div>
            {searchResults.length === 0 ? (
              <div style={{ padding: 12, fontSize: 12, color: '#9e9e9e' }}>No matches.</div>
            ) : searchResults.slice(0, 12).map(r => (
              <button key={r.id} onClick={() => {
                  const c = conversations.find(c => c.id === r.conversation_id);
                  if (c) { openConversation(c); setSearchQ(''); setSearchResults([]); }
                }}
                style={{ display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
                          background: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                <div style={{ fontSize: 11, color: '#9e9e9e' }}>
                  {r.conversation_title} · {r.author_name} · {new Date(r.created_at).toLocaleDateString()}
                </div>
                <div style={{ fontSize: 13, color: '#212121' }}>{r.body}</div>
              </button>
            ))}
          </div>
        )}
        {conversations.length === 0 ? (
          <div className="card text-sm text-gray-500">No conversations yet. Tap + New message to start one.</div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            {conversations.map(c => (
              <div key={c.id}
                style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid #f0f0f0' }}>
                {/* Hide button revealed by left-swipe */}
                <button onClick={() => hideConversation(c)}
                  style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80,
                            background: '#c62828', color: '#fff', border: 'none',
                            fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                            cursor: 'pointer' }}>
                  Hide
                </button>
                <button
                  onClick={() => { if (swipedId === c.id) { setSwipedId(null); return; } openConversation(c); }}
                  onContextMenu={(e) => { e.preventDefault(); setConvoActionFor(c); }}
                  onTouchStart={(e) => { startConvoLongPress(c); onSwipeStart(e, c.id); }}
                  onTouchMove={(e) => onSwipeMove(e, c.id)}
                  onTouchEnd={() => { cancelConvoLongPress(); onSwipeEnd(); }}
                  onTouchCancel={() => { cancelConvoLongPress(); onSwipeEnd(); }}
                  onMouseDown={() => startConvoLongPress(c)}
                  onMouseUp={cancelConvoLongPress}
                  onMouseLeave={cancelConvoLongPress}
                  style={{ display: 'block', width: '100%', textAlign: 'left',
                            padding: '12px 14px',
                            background: selected?.id === c.id ? '#eef2f6' : '#fff',
                            border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                            transform: swipedId === c.id ? 'translateX(-80px)' : 'translateX(0)',
                            transition: 'transform 0.18s ease', position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#212121',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {c.title || 'Conversation'}
                    </div>
                    {c.last_message_at && (
                      <div style={{ fontSize: 10, color: '#9e9e9e', flexShrink: 0 }}>
                        {new Date(c.last_message_at).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#757575', marginTop: 2,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.last_body || <em style={{ color: '#bdbdbd' }}>No messages yet</em>}
                  </div>
                  {c.unread_count > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
                                    background: 'var(--gold, #c8a25b)', color: '#fff', marginTop: 4, display: 'inline-block' }}>
                      {c.unread_count} new
                    </span>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message pane (only when a conversation is selected) */}
      {selected && (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '60vh' }}>
          <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div className="text-sm font-semibold">{selected.title}</div>
            </div>
            <button onClick={() => setSelected(null)} className="btn-secondary" style={{ fontSize: 12 }}>
              ← Back to list
            </button>
          </div>
          <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0, minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {messages.length === 0 ? (
                <div style={{ color: '#9e9e9e', fontSize: 13, textAlign: 'center', padding: 20 }}>
                  No messages yet. Send the first one.
                </div>
              ) : (
                messages.map(m => {
                  const mine = m.author_id === currentUser.id;
                  const deleted = !!m.deleted_at;
                  const isEditing = editing?.message_id === m.id;
                  return (
                    <div key={m.id} style={{ display: 'flex', flexDirection: 'column',
                                                alignItems: mine ? 'flex-end' : 'flex-start' }}>
                      {!mine && <div style={{ fontSize: 10, color: '#9e9e9e', marginLeft: 8 }}>{m.author_name || ''}</div>}
                      <div onTouchStart={() => startLongPress(m)} onTouchEnd={cancelLongPress} onTouchMove={cancelLongPress}
                        onContextMenu={(e) => { e.preventDefault(); if (!deleted) setActionSheet({ message: m }); }}
                        style={{
                        background: mine ? 'var(--forest, #1B2B1B)' : '#f5f5f5',
                        color: mine ? '#fff' : '#212121',
                        padding: '8px 12px', borderRadius: 12, maxWidth: '78%',
                        fontSize: 14, fontStyle: deleted ? 'italic' : 'normal',
                        opacity: deleted ? 0.6 : 1, whiteSpace: 'pre-wrap',
                        userSelect: deleted ? 'none' : 'text',
                      }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <textarea value={editing.body}
                              onChange={e => setEditing(s => ({ ...s, body: e.target.value }))}
                              autoFocus rows={3}
                              style={{ width: '100%', padding: 6, fontSize: 14,
                                        border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4,
                                        background: 'rgba(255,255,255,0.1)', color: 'inherit', fontFamily: 'inherit', resize: 'none' }} />
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <button onClick={() => setEditing(null)}
                                style={{ padding: '4px 8px', fontSize: 11, fontFamily: 'inherit',
                                          background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, cursor: 'pointer' }}>
                                Cancel
                              </button>
                              <button onClick={saveEdit}
                                style={{ padding: '4px 10px', fontSize: 11, fontFamily: 'inherit',
                                          background: '#fff', color: 'var(--forest, #1B2B1B)', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {m.body}
                            {Array.isArray(m.refs) && m.refs.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                                {m.refs.map((r, i) => <ChatRefCard key={i} ref={r} mine={mine} currentUser={currentUser} />)}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div style={{ fontSize: 9, color: '#bdbdbd', marginTop: 1 }}>
                        {new Date(m.created_at).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        {m.edited_at && <span> · edited</span>}
                      </div>
                    </div>
                  );
                })
              )}
              {/* Read receipts under the latest outgoing message. CJ
                  2026-05-16: previously no way to tell if the other
                  side had seen the message. */}
              {(() => {
                const lastMine = [...messages].reverse().find(m => m.author_id === currentUser.id && !m.deleted_at);
                if (!lastMine || reads.length === 0) return null;
                const sentAt = new Date(lastMine.created_at).getTime();
                const seenBy = reads.filter(r =>
                  r.user_id !== currentUser.id && r.last_read_at && new Date(r.last_read_at).getTime() >= sentAt
                );
                const otherParticipants = reads.filter(r => r.user_id !== currentUser.id);
                if (otherParticipants.length === 0) return null;
                const allSeen = seenBy.length === otherParticipants.length;
                const label = seenBy.length === 0
                  ? 'Delivered · not yet read'
                  : allSeen
                    ? (otherParticipants.length === 1 ? `Read by ${seenBy[0].name}` : `Read by everyone`)
                    : `Read by ${seenBy.map(r => r.name).join(', ')}`;
                return (
                  <div style={{ alignSelf: 'flex-end', fontSize: 10, color: '#9e9e9e', marginTop: 2, paddingRight: 4 }}>
                    {seenBy.length > 0 && <span style={{ color: '#1565c0', marginRight: 2 }}>✓</span>}
                    {label}
                  </div>
                );
              })()}
            </div>
            {/* Upload-in-flight indicator (CJ 2026-05-16: was hard to tell
                if an image was actually uploading; the camera button dimming
                wasn't obvious enough). */}
            {uploading && (
              <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600,
                              color: '#1565c0', background: '#e3f2fd',
                              borderTop: '1px solid #bbdefb',
                              display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 99,
                                background: '#1565c0', animation: 'pulse 1.2s ease-in-out infinite' }} />
                Uploading photo…
              </div>
            )}
            {/* Staged refs preview — chips above the composer */}
            {stagedRefs.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 10px',
                              borderTop: '1px solid #f0f0f0', background: uploadOk ? '#dcfce7' : '#fafbfc',
                              transition: 'background 0.3s' }}>
                {uploadOk && (
                  <span style={{ flexBasis: '100%', fontSize: 11, fontWeight: 700,
                                  color: '#15803d', marginBottom: 2 }}>
                    ✓ Photo ready — tap Send to attach
                  </span>
                )}
                {stagedRefs.map((r, i) => (
                  r.kind === 'image' ? (
                    <span key={i}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: '#eef2f6', border: '1px solid #cfd8dc',
                                padding: '3px 4px 3px 3px', borderRadius: 8 }}>
                      <img src={r.url ? `${r.url}${r.url.includes('?') ? '&' : '?'}u=${currentUser.id}` : ''} alt={r.name || 'image'}
                        style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                      <span style={{ fontSize: 11, fontWeight: 600, maxWidth: 100,
                                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.name || 'image'}
                      </span>
                      <button onClick={() => setStagedRefs(s => s.filter((_, j) => j !== i))}
                        style={{ background: 'transparent', border: 'none', color: '#757575',
                                  fontSize: 14, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                    </span>
                  ) : (
                    <span key={i}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: 11, fontWeight: 600,
                                background: '#eef2f6', border: '1px solid #cfd8dc',
                                padding: '3px 8px', borderRadius: 999 }}>
                      {r.kind === 'recipe' ? '🍵' : r.kind === 'task' ? '☑' : '📄'} {r.label}
                      <button onClick={() => setStagedRefs(s => s.filter((_, j) => j !== i))}
                        style={{ background: 'transparent', border: 'none', color: '#757575',
                                  fontSize: 14, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
                    </span>
                  )
                ))}
              </div>
            )}
            {chatEnabled && <div style={{ borderTop: '1px solid #e0e0e0', padding: 8, display: 'flex', gap: 6 }}>
              <button onClick={() => setShowRefPicker(true)} disabled={sending}
                title="Attach a recipe, task, or SOP"
                style={{ padding: '10px 12px', fontSize: 16, fontFamily: 'inherit',
                          background: '#fff', color: '#37474f', border: '1px solid #d0d4d9', borderRadius: 6,
                          cursor: 'pointer' }}>
                📎
              </button>
              <input ref={fileInputRef} type="file" accept="image/*"
                onChange={handlePhotoPick} style={{ display: 'none' }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={sending || uploading}
                title="Attach a photo"
                style={{ padding: '10px 12px', fontSize: 16, fontFamily: 'inherit',
                          background: '#fff', color: '#37474f', border: '1px solid #d0d4d9', borderRadius: 6,
                          cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}>
                {uploading ? '…' : '📷'}
              </button>
              <div style={{ flex: 1, position: 'relative' }}>
                <input value={draft} onChange={e => onDraftChange(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type a message… (use @ to mention someone)"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit' }} />
                {mentionDropdown && (
                  <div style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, right: 0,
                                  background: '#fff', border: '1px solid #d0d4d9', borderRadius: 8,
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  maxHeight: 180, overflowY: 'auto', zIndex: 20 }}>
                    {recipients
                      .filter(u => u.name.toLowerCase().includes(mentionDropdown.query))
                      .slice(0, 6)
                      .map(u => (
                        <button key={u.id} onClick={() => pickMention(u.name)}
                          style={{ display: 'block', width: '100%', textAlign: 'left',
                                    padding: '8px 10px', border: 'none', background: 'transparent',
                                    cursor: 'pointer', fontFamily: 'inherit',
                                    borderBottom: '1px solid #f0f0f0' }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{u.name}</div>
                          <div style={{ fontSize: 11, color: '#9e9e9e' }}>{u.role}</div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <button onClick={send} disabled={sending || (!draft.trim() && stagedRefs.length === 0)}
                style={{ padding: '10px 16px', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                          background: 'var(--forest, #1B2B1B)', color: '#fff', border: 'none', borderRadius: 6,
                          cursor: 'pointer', opacity: (sending || (!draft.trim() && stagedRefs.length === 0)) ? 0.5 : 1 }}>
                {sending ? '…' : 'Send'}
              </button>
            </div>}
            {!chatEnabled && (
              <div style={{ borderTop: '1px solid #e0e0e0', padding: 12, textAlign: 'center', fontSize: 13, color: '#6b5f4a', background: '#faf7f0' }}>
                💬 Chat is archived — <b>read-only</b>. Old messages stay here to read. For anything new, post on <b>The Board</b> 📌.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conversation-list action sheet (long-press / right-click) */}
      {convoActionFor && (
        <div onClick={() => setConvoActionFor(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                    zIndex: 60, padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 420,
                      display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 14, borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{conversationLabel(convoActionFor)}</div>
              <div style={{ fontSize: 11, color: '#9e9e9e' }}>Choose an action</div>
            </div>
            {chatEnabled && <button onClick={() => hideConversation(convoActionFor)}
              style={{ padding: 14, fontSize: 14, fontFamily: 'inherit', textAlign: 'left',
                        background: 'transparent', border: 'none', borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer' }}>
              ➖ Hide from my inbox
              <div style={{ fontSize: 11, color: '#9e9e9e' }}>Comes back if there's a new message.</div>
            </button>}
            {chatEnabled && isManagerCurrentUser && (
              <button onClick={() => wipeConversation(convoActionFor)}
                style={{ padding: 14, fontSize: 14, fontFamily: 'inherit', color: '#dc2626', textAlign: 'left',
                          background: 'transparent', border: 'none', borderBottom: '1px solid #f0f0f0',
                          cursor: 'pointer' }}>
                🗑 Delete entire history (everyone)
                <div style={{ fontSize: 11, color: '#9e9e9e' }}>Permanent. Wipes messages + attachments for all participants.</div>
              </button>
            )}
            <button onClick={() => setConvoActionFor(null)}
              style={{ padding: 14, fontSize: 14, fontFamily: 'inherit', color: '#757575',
                        background: '#fafafa', border: 'none',
                        cursor: 'pointer', borderRadius: '0 0 12px 12px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* New conversation modal */}
      {showCompose && (
        <NewConversationModal currentUser={currentUser}
          onClose={() => setShowCompose(false)}
          onCreated={(conv) => {
            setShowCompose(false);
            setConversations(cs => cs.find(x => x.id === conv.id) ? cs : [conv, ...cs]);
            openConversation(conv);
          }} />
      )}
      {/* Reference picker for inline recipe/task/SOP attachments */}
      {showRefPicker && (
        <ChatRefPicker currentUser={currentUser}
          onClose={() => setShowRefPicker(false)}
          onPick={(ref) => {
            setStagedRefs(s => [...s, ref]);
            setShowRefPicker(false);
          }} />
      )}
      {/* Long-press / right-click action sheet for a message */}
      {actionSheet && (
        <div onClick={() => setActionSheet(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50,
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 8, width: '100%', maxWidth: 340,
                      display: 'flex', flexDirection: 'column' }}>
            {chatEnabled && actionSheet.message.author_id === currentUser.id && (
              <button onClick={() => { setEditing({ message_id: actionSheet.message.id, body: actionSheet.message.body }); setActionSheet(null); }}
                style={{ padding: 14, fontSize: 14, fontFamily: 'inherit',
                          background: 'transparent', border: 'none', textAlign: 'left',
                          borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                ✏️ Edit
              </button>
            )}
            {chatEnabled && (
              <button onClick={() => deleteMessage(actionSheet.message)}
                style={{ padding: 14, fontSize: 14, fontFamily: 'inherit', color: '#dc2626',
                          background: 'transparent', border: 'none', textAlign: 'left',
                          cursor: 'pointer' }}>
                🗑 Delete
              </button>
            )}
            <button onClick={() => setActionSheet(null)}
              style={{ padding: 14, fontSize: 14, fontFamily: 'inherit', color: '#757575',
                        background: '#fafafa', border: 'none', borderTop: '1px solid #f0f0f0',
                        cursor: 'pointer', borderRadius: '0 0 12px 12px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {err && <div className="card" style={{ borderLeft: '4px solid #c62828' }}><div className="text-xs text-red-700">{err}</div></div>}
    </div>
  );
}
